// Live-Ansicht (Nico, 12.09.2026: „was gerade live auf meiner Seite passiert").
//
// Quelle ist das eigene Tracking (t.js → /api/track → analytics_events), nicht
// Google Analytics. t.js schickt jeden Seitenaufruf sofort ab; was fehlte, war
// nur die Ansicht darauf.
//
// Aufbau nach dem abgestimmten Entwurf (13.09.2026):
//
//  - Eine Zeile pro Besuch mit festen Spalten, damit das Auge senkrecht
//    runterläuft. Der Website-Name steht bewusst NICHT zuerst — das ist die
//    Information, die am wenigsten überrascht. Vorn steht die Herkunft.
//  - Ein Zeitstrahl in der Wann-Spalte statt bloßer Zeitangaben.
//  - Ein Klartextsatz unter der Zeile, aber NUR wenn er etwas sagt, das in den
//    Spalten nicht steht (Kontaktklick, mehrere Seiten, Sofort-Absprung).
//    Sonst wäre er Doppelung und würde die Dichte auffressen, die den Sinn der
//    Liste ausmacht.
//  - Der Kontaktklick ist das Lauteste auf der Seite. Er ist das Einzige hier,
//    das Geld bedeutet.
//  - Kein zweiter Ereignisstrom: Er wiederholte, was in den Zeilen schon steht.
//    Den Takt trägt die Pulslinie oben.
//
//  - In der Zeile steht die SITZUNG, sonst nichts. Was der Besucher gerade
//    genau macht — Abschnitt, Verweildauer darin, Klicks in Reihenfolge —
//    steht ausschließlich in der Fläche, die sich beim Klick öffnet
//    (Nico, 12.09.2026). In der Zeile bleibt davon ein kleines Live-Zeichen:
//    ein Blatt Papier in Daumengröße mit einem Strich an der Stelle, an der er
//    gerade liest. Es blinkt auf, sobald sich etwas bewegt.
//
// Nachgeladen wird nur, wenn wirklich jemand da ist: alle 5 Sekunden, solange
// jemand online ist, sonst einmal pro Minute — das reicht, um zu merken, dass
// gerade jemand gekommen ist. Kein Websocket: Bei Nicos Zahlen ist eine
// Abfrage billiger als eine offene Verbindung — und sie geht auch, wenn der
// Laptop zwischendurch schläft.

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight, Radio } from 'lucide-react';
import { getLive, type LiveBesucher } from '@/lib/analytics-api';
import Sitzung from '@/components/live/Sitzung';
import {
  AktionIcon, dauer, GeraetIcon, Herkunft, istKontakt, LiveWidget, ruhtGerade, uhrzeit, vorhin,
} from '@/components/live/bausteine';

const FENSTER = [
  { key: 15, label: '15 Min' },
  { key: 30, label: '30 Min' },
  { key: 60, label: '1 Std' },
  { key: 180, label: '3 Std' },
];

/** Spaltenraster — einmal definiert, von Kopfzeile und Zeilen benutzt. */
// „gerade eben" braucht 78 px — mit 74 brach die Spalte ausgerechnet bei den
// Live-Zeilen um und machte sie 19 px höher als alle anderen.
const RASTER = 'grid grid-cols-[88px_18px_42px_minmax(0,1.3fr)_176px_minmax(0,1fr)_118px_146px] gap-3.5 items-center';

/**
 * Der Klartextsatz. Gibt null zurück, wenn er nichts beitragen würde — das ist
 * die eigentliche Regel dieser Ansicht, nicht der Satz selbst.
 */
function satzZu(b: LiveBesucher): JSX.Element | null {
  const kontakt = b.aktionen.filter(istKontakt);
  const tat = kontakt.includes('tel') ? 'die Nummer gewählt' : 'die E-Mail-Adresse angeklickt';
  if (kontakt.length) {
    return (
      <>
        {b.quelle ? <>Über <strong className="font-semibold">{b.kanal === 'paid' ? 'eine Anzeige' : b.kanal === 'organic' ? 'die Google-Suche' : b.quelle}</strong> gekommen, </> : null}
        {/* Die Lesezeit trifft erst beim Verlassen ein. Wer klickt und dann
            bleibt, hat sie noch nicht — „0 s auf der Seite" wäre in der
            lautesten Zeile der Ansicht eine erfundene Zahl, und sie widerspräche
            dem „—" in der Spalte „Gelesen" derselben Zeile. */}
        {b.lesezeit_s > 0
          ? <><strong className="font-semibold">{dauer(b.lesezeit_s)}</strong> auf der Seite — und dann {tat}.</>
          : <>und hat {tat}. Wie lange er gelesen hat, meldet der Tracker erst beim Verlassen.</>}
      </>
    );
  }
  if (b.seiten >= 3) {
    // Die Mitte ist alles ZWISCHEN erster und letzter Seite — sonst stand die
    // Einstiegsseite zweimal im selben Satz, und zwar im häufigsten Fall
    // (genau drei Seiten).
    const mitte = b.weg.slice(1, -1).slice(-2);
    return <>Von <strong className="font-semibold">{b.weg[0]}</strong>{mitte.length ? <> über {mitte.join(', ')}</> : null} bis <strong className="font-semibold">{b.weg[b.weg.length - 1]}</strong>{b.scroll_max != null ? <> — bis {b.scroll_max} % gescrollt</> : null}.</>;
  }
  // Sofort wieder weg: nur behaupten, wenn wirklich eine Lesezeit gemeldet
  // wurde. Ohne sie weiß man es schlicht nicht.
  if (b.lesezeit_s > 0 && b.lesezeit_s < 10 && b.seiten <= 1) {
    return <>{b.kanal === 'paid' ? 'Die Anzeige hat den Klick gebracht — ' : ''}nach <strong className="font-semibold">{dauer(b.lesezeit_s)}</strong> war er wieder weg{b.scroll_max != null && b.scroll_max < 15 ? ', ohne zu scrollen' : ''}.</>;
  }
  return null;
}

function Pulslinie({ takt }: { takt: number[] }) {
  const max = Math.max(1, ...takt);
  const punkte = takt.map((n, i) => {
    const x = takt.length > 1 ? (i / (takt.length - 1)) * 600 : 600;
    return `${Math.round(x)},${Math.round(40 - (n / max) * 34)}`;
  });
  const letzter = punkte[punkte.length - 1]?.split(',') || ['600', '40'];
  return (
    <svg viewBox="0 0 600 44" preserveAspectRatio="none" className="mt-1 block h-11 w-full" aria-hidden="true">
      <polyline points={punkte.join(' ')} fill="none" stroke="#2563eb" strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={letzter[0]} cy={letzter[1]} r="3" fill="#2563eb" />
    </svg>
  );
}

function Zeile({ b, heute, onOeffnen }: { b: LiveBesucher; heute: string; onOeffnen: () => void }) {
  const kontakt = b.aktionen.some(istKontakt);
  const satz = satzZu(b);
  const weg = b.weg.slice(-2);
  // Noch da, tut aber nichts: Der Tab ist offen, seit anderthalb Minuten
  // bewegt sich nichts. Das ist etwas anderes als „liest gerade".
  const ruht = ruhtGerade(b.jetzt);
  return (
    <button
      type="button"
      onClick={onOeffnen}
      className={`${RASTER} w-full border-b border-gray-100 px-[18px] py-3 text-left last:border-b-0 ${
        kontakt ? 'bg-blue-50 shadow-[inset_3px_0_0_#2563eb] hover:bg-blue-100/70' : 'hover:bg-gray-50'
      }`}
      title="Sitzung ansehen"
    >
      {/* Hier steht die letzte BEWEGUNG, nicht das letzte Lebenszeichen — sonst
          stünde über einem seit zwanzig Minuten geparkten Tab „gerade eben".
          Danach ist die Liste auch sortiert. */}
      <span
        className={`text-right text-[13px] tabular-nums ${b.online && !ruht ? 'font-medium text-emerald-700' : 'text-gray-600'}`}
        title={b.online ? `Letzte Bewegung ${uhrzeit(b.bewegt)} Uhr · zuletzt gesehen ${uhrzeit(b.zuletzt)} Uhr` : undefined}
      >
        {vorhin(b.bewegt, heute)}
        <span className="block text-xs font-normal text-gray-400">{uhrzeit(b.bewegt)}</span>
      </span>

      {/* Zeitstrahl: die Linie läuft durch alle Zeilen, der Punkt sitzt darauf */}
      <span className="relative flex h-full justify-center self-stretch">
        <span className="absolute inset-y-0 w-0.5 bg-gray-200" />
        <span className={`relative mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full border-[3px] ${
          kontakt ? 'border-blue-50 bg-blue-600'
            : ruht ? 'border-white bg-emerald-200'
            : b.online ? `border-white bg-emerald-500${b.jetzt ? ' animate-pulse' : ''}`
            : 'border-white bg-gray-300'
        }`} />
      </span>

      <LiveWidget jetzt={b.jetzt} />

      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Herkunft kanal={b.kanal} quelle={b.quelle} kampagne={b.kampagne} />
        {b.keyword && (
          <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-900 ring-1 ring-amber-200">„{b.keyword}“</span>
        )}
        {/* Gerät und Land stehen hier mit drin, nicht in einer eigenen Zeile:
            Eine zweite Zeile je Besuch nur für ein Icon und „DE" hat die Liste
            doppelt so hoch gemacht, ohne etwas zu sagen. */}
        <span className="inline-flex shrink-0 items-center gap-1 text-xs text-gray-400" title={`${b.device || 'unbekanntes Gerät'}${b.land ? ` · ${b.land}` : ''}`}>
          <GeraetIcon device={b.device} />
          {b.land || ''}
        </span>
      </span>

      <span className="truncate text-[13px] text-gray-600" title={b.site_name}>{b.site_name}</span>

      <span className="flex min-w-0 items-center gap-1.5">
        {b.weg.length > weg.length && <span className="shrink-0 text-xs text-gray-400">…</span>}
        {weg.map((p, i) => (
          <span key={`${p}-${i}`} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-gray-300" />}
            <span className={`truncate rounded px-1.5 py-0.5 text-xs ${i === weg.length - 1 ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`}>{p}</span>
          </span>
        ))}
        {!weg.length && b.pfad && <span className="truncate rounded bg-gray-900 px-1.5 py-0.5 text-xs text-white">{b.pfad}</span>}
        {b.online && b.seiten <= 1 && (
          ruht
            ? <span className="shrink-0 text-xs text-gray-500">Tab noch offen</span>
            : <span className="shrink-0 text-xs font-medium text-emerald-700">ist noch da</span>
        )}
      </span>

      <span>
        {/* Nur die GEMESSENE Lesezeit. Sie trifft erst ein, wenn jemand die
            Seite verlässt — was er bis dahin tut, zeigt das Live-Zeichen links
            und in voller Länge die Sitzungsfläche. */}
        {b.lesezeit_s > 0 ? (
          <>
            <span className="text-[13px] tabular-nums text-gray-700">{dauer(b.lesezeit_s)}</span>
            <span className="mt-1 block h-[3px] rounded bg-gray-200">
              <span className="block h-[3px] rounded bg-gray-500" style={{ width: `${b.scroll_max ?? 0}%` }} />
            </span>
          </>
        ) : (
          <span className="text-[13px] text-gray-400">—</span>
        )}
      </span>

      <span className="flex flex-wrap gap-1">
        {b.anfrage ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white">Anfrage geschickt</span>
        ) : kontakt ? (
          b.aktionen.filter(istKontakt).map((a) => (
            <span key={a} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white">
              <AktionIcon name={a} /> {a === 'tel' ? 'angerufen' : 'gemailt'}
            </span>
          ))
        ) : b.seiten > 1 ? (
          <span className="text-xs text-gray-500">{b.seiten} Seiten gelesen</span>
        ) : null}
      </span>

      {satz && (
        <span className={`col-start-4 col-end-[-1] mt-1.5 text-[14px] leading-snug ${kontakt ? 'text-blue-900' : 'text-gray-600'}`}>
          {satz}
          {kontakt && (
            <span className="ml-2 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">Das ist ein Anruf wert</span>
          )}
        </span>
      )}

    </button>
  );
}

export default function LivePage() {
  const [minuten, setMinuten] = useState(30);
  const [site, setSite] = useState('');
  const [sitzung, setSitzung] = useState<string | null>(null);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['live', minuten, site],
    queryFn: () => getLive(minuten, site),
    // „Die Seite live soll sich nur aktualisieren, wenn Nutzer da sind."
    // (Nico, 12.09.2026). Solange jemand online ist, alle 5 Sekunden — dann
    // bewegt sich das Live-Zeichen sichtbar mit. Ist niemand da, reicht ein
    // Blick pro Minute; ganz abschalten geht nicht, sonst merkt die Seite nie,
    // dass gerade jemand gekommen ist.
    refetchInterval: (q) => ((q.state.data?.online ?? 0) > 0 ? 5_000 : 60_000),
    refetchIntervalInBackground: false,
    // Im Hintergrund wird nicht nachgeladen — beim Zurueckkommen waeren die
    // Zahlen sonst bis zu einer Minute alt und stuenden trotzdem unter „live".
    refetchOnWindowFocus: true,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });

  const heute = data?.jetzt || new Date().toISOString();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-semibold tracking-tight text-gray-900">
            <Radio className={`h-5 w-5 ${isFetching ? 'animate-pulse text-emerald-500' : 'text-gray-400'}`} />
            Live
          </h1>
          <p className="mt-1 text-[13px] text-gray-500">
            Wer gerade auf den Websites unterwegs ist, woher er kommt und was er anklickt.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {FENSTER.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setMinuten(f.key)}
              className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                minuten === f.key ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{(error as Error).message}</div>}
      {isLoading && !data && <div className="py-10 text-center text-sm text-gray-500">Wird geladen …</div>}

      {data && (
        <>
          {/* Pulsleiste statt drei Kacheln: eine Zahl, ein Takt, drei Nebenzahlen */}
          <div className="flex flex-wrap items-center gap-x-7 gap-y-4 rounded-xl border border-gray-200 bg-white px-5 py-4">
            <div className="flex items-baseline gap-2.5">
              <span className="text-[40px] font-semibold leading-none tracking-tight tabular-nums text-gray-900">{data.online}</span>
              <span className={`h-2.5 w-2.5 rounded-full ${data.online > 0 ? 'animate-pulse bg-emerald-500 ring-4 ring-emerald-500/15' : 'bg-gray-300'}`} />
              <div className="ml-0.5">
                <div className="text-sm font-medium text-gray-900">gerade online</div>
                <div className="text-xs text-gray-400">In den letzten {data.online_minuten} Minuten aktiv</div>
              </div>
            </div>

            <div className="hidden h-10 w-px bg-gray-100 sm:block" />

            <div className="min-w-[200px] flex-1">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Seitenaufrufe je Minute</span>
                <span className="text-xs tabular-nums text-gray-400">vor {data.fenster_minuten} Min · jetzt</span>
              </div>
              <Pulslinie takt={data.takt || []} />
            </div>

            <div className="hidden h-10 w-px bg-gray-100 sm:block" />

            <div className="flex gap-6">
              <div>
                <div className="text-xl font-semibold tabular-nums text-gray-900">{data.aktiv}</div>
                <div className="text-xs text-gray-500">Besucher</div>
              </div>
              <div>
                <div className="text-xl font-semibold tabular-nums text-gray-900">{data.seitenaufrufe}</div>
                <div className="text-xs text-gray-500">Seitenaufrufe</div>
              </div>
              <div>
                <div className={`text-xl font-semibold tabular-nums ${data.kontaktklicks > 0 ? 'text-blue-700' : 'text-gray-900'}`}>{data.kontaktklicks ?? 0}</div>
                <div className="text-xs text-gray-500">mit Kontaktklick</div>
              </div>
            </div>
          </div>

          {data.sites.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setSite('')}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${!site ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'}`}
              >
                Alle Seiten
              </button>
              {data.sites.map((s) => (
                <button
                  key={s.slug}
                  type="button"
                  onClick={() => setSite(s.slug)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${site === s.slug ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'}`}
                >
                  {s.name}
                  {s.online > 0 && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                  <span className="tabular-nums opacity-60">{s.aktiv}</span>
                </button>
              ))}
            </div>
          )}

          {data.aktiv === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-600">
              In den letzten {data.fenster_minuten} Minuten war niemand da.
              {site ? ' Auf dieser Seite jedenfalls — probier „Alle Seiten".' : ' Zeitfenster verlängern, dann sieht man mehr.'}
            </div>
          ) : (
            // Seitwärts scrollen statt zerquetschen: Die beiden flexiblen
            // Spalten trugen die gesamte Verknappung, während „Gelesen" und
            // „Hat gemacht" starr blieben — zwischen 1024 und 1125 px lief der
            // Kanal-Chip über den Website-Namen, darunter fielen die rechten
            // Spalten ohne Querbalken einfach weg.
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <div className="min-w-[820px]">
              <div className={`${RASTER} border-b border-gray-100 px-[18px] py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-400`}>
                <span className="text-right">Wann</span>
                <span />
                <span
                  className="text-center"
                  title="Wo der Besucher gerade auf der Seite steht. Der Strich wandert mit, der Rahmen blinkt bei jeder Bewegung. Kein Kästchen heißt: nicht mehr da."
                >
                  Jetzt
                </span>
                <span>Woher</span>
                <span>Website</span>
                <span>Weg über die Seite</span>
                <span>Gelesen</span>
                <span>Hat gemacht</span>
              </div>
              {data.besucher.map((b) => (
                <Zeile key={b.id} b={b} heute={heute} onOeffnen={() => setSitzung(b.id)} />
              ))}
              {data.aktiv > data.besucher.length && (
                <p className="px-[18px] py-2.5 text-xs text-gray-500">
                  … und {data.aktiv - data.besucher.length} weitere im Zeitfenster.
                </p>
              )}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400">
            Aus dem eigenen Tracking, kein Google Analytics. Besucher werden über einen Tageshash gruppiert — keine Cookies,
            keine gespeicherte IP. Das Kästchen in der Spalte „Jetzt" ist die Seite, der Strich darin zeigt, wo der
            Besucher gerade liest; der Rahmen blinkt, sobald er sich bewegt. Grau heißt: Der Tab ist noch offen, aber
            seit über einer Minute bewegt sich nichts. Sortiert ist nach der letzten Bewegung, nicht nach dem letzten
            Lebenszeichen — ein geparkter Tab meldet sich jede Minute und stünde sonst dauerhaft ganz oben. Eine Zeile
            ist ein Besuch: 30 Minuten ohne ein Ereignis beenden ihn, danach fängt ein neuer an.
            Lesezeit und Scrolltiefe meldet der Tracker erst, wenn jemand die Seite verlässt.
            {data.online > 0 ? ' Lädt alle 5 Sekunden nach, solange jemand online ist.' : ' Lädt einmal pro Minute nach, solange niemand da ist.'}
            {data.gekappt && ' Im Zeitfenster liegen mehr Ereignisse, als geladen wurden — die Liste ist gekürzt.'}
          </p>
        </>
      )}

      {sitzung && <Sitzung id={sitzung} onClose={() => setSitzung(null)} />}
    </div>
  );
}
